import React from 'react'
import { PrimaryButton, SecondaryButton } from '../../../components/Button'
import Modal from '../../../components/Modal'
import { SectionBreakerHorizontal, SubTitle } from '../../../components/Shared'
import { PayoutType } from './payoutTypes'
import { RADOM_COLORS } from '../../../util/Constants'
import Checkbox from '../../../components/Checkbox'
import { ManagedPaymentMethodSelector } from '../../../components/ManagedMethodMultiselector'
import { InputLabel } from '../../../components/Input'
import SearchMultiselect from '../../../components/SearchMultiselect'
import { AddButton } from '../../../components/CreateDepositAddressModal'
import CustomerForm from '../../../components/CustomerForm'
import { ManagedPaymentMethod } from '../../../util/Managed'
import { Customer, ExternalAccount } from '../../../state/Radom'
import { Currencies, Currency } from '../../../util/Currencies'
import Dropdown, { DropdownItem } from '../../../components/Dropdown'
import ExternalAccountSelectorTag from './ExternalAccountSelectorTag'

interface ISelectAssetsProps {
  payoutType: PayoutType
  setPayoutType: (type: PayoutType) => void
  testMode: boolean
  selectedFundingMethod: ManagedPaymentMethod | undefined
  setSelectedFundingMethod: (method: ManagedPaymentMethod | undefined) => void
  setSelectedPayoutMethod: (method: ManagedPaymentMethod | Currency | undefined) => void
  selectedPayoutMethod: ManagedPaymentMethod | Currency | undefined
  isLoadingCustomer: boolean
  customers: Customer[]
  selectedCustomer: Customer | null
  setSelectedCustomer: (customer: Customer | null) => void
  customerFormVisible: boolean
  setCustomerFormVisible: (visible: boolean) => void
  loadCustomers: () => Promise<void>
  onContinue: () => void
  // External account props for fiat
  externalAccounts: ExternalAccount[]
  selectedExternalAccount: ExternalAccount | undefined
  setSelectedExternalAccount: (account: ExternalAccount | undefined) => void
  isLoadingExternalAccounts: boolean
}

const SelectAssets = ({
  payoutType,
  setPayoutType,
  testMode,
  selectedFundingMethod,
  setSelectedFundingMethod,
  selectedPayoutMethod,
  setSelectedPayoutMethod,
  isLoadingCustomer,
  customers,
  selectedCustomer,
  setSelectedCustomer,
  customerFormVisible,
  setCustomerFormVisible,
  loadCustomers,
  onContinue,
  externalAccounts,
  selectedExternalAccount,
  setSelectedExternalAccount,
  isLoadingExternalAccounts
}: ISelectAssetsProps): JSX.Element => {
  const isFiat = payoutType === PayoutType.Fiat

  // Filter external accounts by payout type
  const filteredExternalAccounts = isFiat
    ? externalAccounts.filter(a => a.accountType !== 'crypto')
    : externalAccounts.filter(a => a.accountType === 'crypto')

  return (
    <>
      <div style={{ maxWidth: 560, width: '100%', display: 'flex', flexDirection: 'column', gap: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <SubTitle>What currencies are you paying out?</SubTitle>
          <div style={{ fontSize: 14, display: 'flex', justifyContent: 'space-between', gap: 20 }}>
            <SecondaryButton
              onClick={() => {
                setPayoutType(PayoutType.Crypto)
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: 0,
                padding: 5,
                color: payoutType === PayoutType.Crypto
                  ? RADOM_COLORS.BLACK
                  : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox size={30} color={RADOM_COLORS.BLUE4}
                checked={payoutType === PayoutType.Crypto} />
              <div style={{ display: 'flex', gap: 4, flexDirection: 'column', alignItems: 'flex-start' }}>
                <span>{PayoutType.Crypto}</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                      Payout to a crypto address
                </span>
              </div>
            </SecondaryButton>

            <SecondaryButton
              onClick={() => {
                setPayoutType(PayoutType.Fiat)
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: 0,
                padding: 5,
                color: payoutType === PayoutType.Fiat
                  ? RADOM_COLORS.BLACK
                  : RADOM_COLORS.GRAY_DARKEST
              }}
              type='button'>
              <Checkbox size={30} color={RADOM_COLORS.BLUE4}
                checked={payoutType === PayoutType.Fiat} />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span>{PayoutType.Fiat}</span>
                <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
            Payout to a bank account
                </span>
              </div>
            </SecondaryButton>
          </div>
        </div>
        <div style={{ display: 'flex', padding: 33, flexDirection: 'column', borderRadius: 16, border: `1px solid ${RADOM_COLORS.GRAY_DARK}`, gap: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h1 style={{ fontSize: 20, fontWeight: 400, lineHeight: '28px', margin: 0 }}>{payoutType} Payout</h1>
            <SubTitle>{payoutType === PayoutType.Crypto ? 'Send crypto directly to an address' : 'Convert crypto to fiat and send to a bank account'}</SubTitle>
          </div>
          <SectionBreakerHorizontal />
          <div style={{ fontSize: 14 }}>

            <ManagedPaymentMethodSelector
              height={25}
              showLabel
              hideDisabled
              disabled={isFiat}
              placeholderText='Select asset'
              selectedMethods={selectedFundingMethod ? [selectedFundingMethod] : []}
              setSelectedMethods={(methods) => {
                setSelectedFundingMethod(methods[methods.length - 1])
              }}
              testnetOnly={testMode}
              label='Funding asset'
              hideCheckbox
              allowedNetworks={!isFiat ? ['Ethereum', 'Tron', 'SepoliaTestnet'] : undefined}
            />
          </div>
          {/* Payout Asset selector — only for fiat */}
          { isFiat && (
            <div style={{ fontSize: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <InputLabel>Payout Asset</InputLabel>
                <SearchMultiselect
                  outerStyle={{ height: 25 }}
                  disabled={false}
                  placeholder="Select asset"
                  itemArray={Currencies.filter(c => c.ticker === 'EUR')}
                  selectedItems={
                    selectedPayoutMethod && 'icon' in selectedPayoutMethod ? [selectedPayoutMethod] : []
                  }
                  setSelectedItems={(c) => setSelectedPayoutMethod(c[0])}
                  keyExtractor={(c) => c.ticker}
                  labelExtractor={(c) => (
                    <div
                      style={{ display: 'flex', gap: 5, alignItems: 'center' }}
                    >
                      {c.icon}
                      {c.ticker}
                    </div>
                  )}
                  isSingleSelect
                />
              </div>
            </div>)}
          <SectionBreakerHorizontal/>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <InputLabel>Customer</InputLabel>

            <SearchMultiselect
              isSingleSelect={true}
              outerStyle={{ height: 25 }}
              isLoading={isLoadingCustomer}
              loadInsideList={true}
              placeholder="Select customer"
              dropdownPlaceholder="No customers found"
              itemArray={customers}
              selectedItems={selectedCustomer ? [selectedCustomer] : []}
              setSelectedItems={newItems => {
                setSelectedCustomer(newItems[0])
                setSelectedExternalAccount(undefined)
              }}
              headers={[
                <AddButton key="create_customer" onClick={() => setCustomerFormVisible(true)}>
                  <span>+ Create customer</span>
                </AddButton>
              ]}
              keyExtractor={(customer) => `${customer.id}${customer.name}${customer.email}`}
              labelExtractor={customer => customer.name ? `${customer.name} (${customer.email})` : customer.email}
              onNewItem={() => setCustomerFormVisible(true)}
            />
          </div>

          {/* External account selector — shown when customer is selected */}
          {selectedCustomer && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <InputLabel>{isFiat ? 'External account' : 'Payout address'}</InputLabel>
              {isLoadingExternalAccounts
                ? <span style={{ fontSize: 13, color: RADOM_COLORS.GRAY_DARKEST }}>Loading accounts...</span>
                : filteredExternalAccounts.length === 0
                  ? <span style={{ fontSize: 13, color: RADOM_COLORS.GRAY_DARKEST }}>
                    No {isFiat ? 'fiat' : 'crypto'} external accounts found for this customer. Please create one first.
                  </span>
                  : <Dropdown
                    selectedContent={
                      selectedExternalAccount
                        ? <ExternalAccountSelectorTag account={selectedExternalAccount} />
                        : <div style={{ display: 'flex', alignItems: 'center', height: 36, opacity: 0.5, fontSize: 12, fontWeight: 500, paddingLeft: 6 }}>
                          {isFiat ? 'Select external account' : 'Select payout address'}
                        </div>
                    }
                    dropdownContent={
                      <div>
                        {filteredExternalAccounts.map((account) => (
                          <DropdownItem key={account.id} onClick={() => setSelectedExternalAccount(account)}>
                            <ExternalAccountSelectorTag account={account} />
                          </DropdownItem>
                        ))}
                      </div>
                    }
                  />
              }
            </div>
          )}

          <PrimaryButton onClick={onContinue}>
            Continue
          </PrimaryButton>

        </div>

      </div>
      <Modal
        visible={customerFormVisible}
        onClose={() => setCustomerFormVisible(false)}
        title='Create customer'>
        <div style={{ padding: '0px 30px 30px 30px', overflow: 'scroll' }}>
          <CustomerForm onCreateCustomer={loadCustomers} />
        </div>
      </Modal>
    </>
  )
}

export default SelectAssets
